import React, { useCallback } from "react";
import { useNavigate } from "react-router";
import { Button, Dialog, useModals, FeatureIcon, Icon } from "@gusarov-studio/rubik-ui";
import type { Database } from "../../../supabase";
import { useHardDeleteResourceMutation } from "../api/resourcesApi";
import { useCurrentFileId } from "../../../router/hooks/useCurrentFileId";
import { routesMap } from "../../../router/routesMap";

interface HardDeleteResourceDialogContainerProps {
  resource: Database["public"]["Tables"]["resources"]["Row"];
  onSubmit?: () => void;
  onCancel?: () => void;
}

const HardDeleteResourceDialogContainer: React.FC<HardDeleteResourceDialogContainerProps> = ({
  resource,
  onSubmit,
  onCancel,
}) => {
  const modals = useModals();
  const navigate = useNavigate();
  const currentFileId = useCurrentFileId();
  const [hardDeleteResource, { isLoading: isDeleting }] = useHardDeleteResourceMutation();

  const closeDialog = useCallback(() => {
    modals.close();

    if (onCancel) {
      onCancel();
    }
  }, [modals, onCancel]);

  const del = useCallback(async () => {
    await hardDeleteResource({
      resourceId: resource.id,
    });

    if (currentFileId === resource.id) {
      navigate(routesMap.newFile.url());
    }

    closeDialog();

    if (onSubmit) {
      onSubmit();
    }
  }, [closeDialog, currentFileId, hardDeleteResource, navigate, onSubmit, resource.id]);

  return (
    <Dialog.Root open={true}>
      <Dialog.Content width="400px">
        <Dialog.Decoration
          type="circles"
          align="left"
        />
        <Dialog.Close
          onClick={closeDialog}
          disabled={isDeleting}
        />
        <Dialog.Header>
          <FeatureIcon
            shape="circle"
            size="48"
            color="danger"
          >
            <Icon
              glyph="trash-01"
              color="danger"
            />
          </FeatureIcon>
          <Dialog.Title>Delete forever?</Dialog.Title>

          <Dialog.Description>
            {`"${resource.name}" will be deleted forever. This can not be undone`}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <Button
            appearance="outline"
            variant="secondary"
            size="40"
            onClick={closeDialog}
            disabled={isDeleting}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            size="40"
            onClick={del}
            disabled={isDeleting}
          >
            Delete forever
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export { HardDeleteResourceDialogContainer };
